import React, { useState, useEffect } from 'react';
import {
  Container,
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Alert,
  IconButton,
  CircularProgress,
} from '@mui/material';
import {
  Schedule as ScheduleIcon,
  Refresh as RefreshIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Preview as PreviewIcon,
  Send as SendIcon,
  LocationOn as LocationOnIcon,
  School as SchoolIcon,
  AccessTime as AccessTimeIcon,
  Computer as ComputerIcon,
  Pool as PoolIcon,
  Class as ClassIcon,
} from '@mui/icons-material';
import axios from 'axios';

// Types
interface JobMapping {
  regular_jobs: { [courseType: string]: { [location: string]: string } };
  zoom_jobs: { [courseType: string]: string };
  total_regular_jobs: number;
  total_zoom_jobs: number;
}

interface ShiftPreview {
  id: string;
  courseType: string;
  location: string;
  shiftType: 'Online' | 'Classroom' | 'Pool';
  startTime: string;
  endTime: string;
  startDate: string;
  endDate: string;
  duration: string;
  classroomId: string;
  sessionIds: string[];
  isEdited?: boolean;
}

interface ShiftCreationResult {
  success: boolean;
  classroom_id: string;
  classroom_name: string;
  sessions_processed: number;
  shifts_created: number;
  created_shifts: any[];
  error?: string;
  error_details?: any;
}

const ConnectTeamScheduler: React.FC = () => {
  
  
  // Selection states
  const [selectedCourseType, setSelectedCourseType] = useState<string>('');
  const [selectedLocation, setSelectedLocation] = useState<string>('');
  const [jobMappings, setJobMappings] = useState<JobMapping | null>(null);
  
  // Preview states
  const [shiftPreviews, setShiftPreviews] = useState<ShiftPreview[]>([]);
  const [editingShift, setEditingShift] = useState<string | null>(null);
  const [editedShifts, setEditedShifts] = useState<{ [id: string]: Partial<ShiftPreview> }>({});
  
  // UI states
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [connectionStatus, setConnectionStatus] = useState<'unknown' | 'connected' | 'error'>('unknown');
  
  // Creation states
  const [creating, setCreating] = useState(false);
  const [creationResults, setCreationResults] = useState<ShiftCreationResult | null>(null);
  const [resultsDialogOpen, setResultsDialogOpen] = useState(false);

  useEffect(() => {
    loadJobMappings();
    testConnection();
  }, []);

  const testConnection = async () => {
    try {
      await axios.get('/api/connecteam/test-connection');
      setConnectionStatus('connected');
    } catch (error) {
      setConnectionStatus('error');
    }
  };

  const loadJobMappings = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await axios.get('/api/connecteam/jobs/mapping');
      
      if (response.data.success) {
        setJobMappings(response.data);
      } else {
        setError('Failed to load job mappings');
      }
    } catch (error: any) {
      console.error('Error loading job mappings:', error);
      setError(error.response?.data?.error || 'Failed to load job mappings');
    } finally {
      setLoading(false);
    }
  };

  // Helper function to map template class types to ConnectTeam course types
  const mapTemplateTypeToConnectTeamType = (templateType: string): string => {
    if (!templateType) return 'Unknown';
    
    // Mapping from template class_type to ConnectTeam course types
    const mappings: { [key: string]: string } = {
      'FAI': 'SFA',  // FAI templates map to SFA in ConnectTeam
      'First Aid': 'SFA',  // First Aid templates also map to SFA
      'Trainer': 'Trainer',
      'NL': 'NL',
      'NLR': 'NLR',
      'NLI': 'NLI',
      'SWI': 'SWI',
      'Bronze': 'Bronze',
      'Examiner': 'Examiner',
      'EFA': 'EFA'
    };
    
    // Try exact match first
    if (mappings[templateType]) {
      return mappings[templateType];
    }
    
    // Try partial matches
    const typeLower = templateType.toLowerCase();
    if (typeLower.includes('first aid') || typeLower.includes('fai')) return 'SFA';
    if (typeLower.includes('trainer')) return 'Trainer';
    if (typeLower.includes('national lifeguard') && typeLower.includes('recert')) return 'NLR';
    if (typeLower.includes('national lifeguard')) return 'NL';
    if (typeLower.includes('swim') && typeLower.includes('instructor')) return 'SWI';
    if (typeLower.includes('bronze')) return 'Bronze';
    if (typeLower.includes('examiner')) return 'Examiner';
    
    // Default: return the original type
    console.warn(`Unknown template type for ConnectTeam mapping: "${templateType}"`);
    return templateType;
  };

  // TODO: Re-enable session datetime parsing when we load actual session data from Salesforce
  // const parseSessionDateTime = (session: any, isEndTime: boolean = false) => { ... }

  // TODO: Re-enable session grouping when we load actual course/session data from Salesforce
  // const groupSessionsByStartTime = (sessions: any[]) => { ... }

  const loadShiftPreviews = async () => {
    if (!selectedCourseType || !selectedLocation) return;
    
    try {
      setLoading(true);
      setError(null);
      
      console.log(`Loading shift previews for ${selectedCourseType} at ${selectedLocation}`);
      
      // Load LSS Tools templates from Salesforce (these are the blueprints for creating shifts)
      console.log('Loading templates from Salesforce...');
      const templatesResponse = await axios.get('/api/course_upload/templates');
      
      // Templates contain the classroom info, courses, and session patterns we need
      const coursesResponse = { data: [] };
      const sessionsResponse = { data: [] };
      
      // Extract templates from Salesforce response
      const templates = templatesResponse.data || [];
      const courses = coursesResponse.data || [];
      const sessions = sessionsResponse.data || [];
      
      console.log(`Found ${templates.length} Salesforce templates, ${courses.length} courses, ${sessions.length} sessions`);
      
      // Convert templates to classroom format and filter
      const matchingClassrooms = templates.filter((template: any) => {
        // Extract course type from template classroom info and map it to ConnectTeam type
        const templateType = template.classroom_info?.class_type || 'Unknown';
        const courseType = mapTemplateTypeToConnectTeamType(templateType);
        
        // Extract location from template classroom info
        const location = template.classroom_info?.location || 'Unknown';
        
        // Check matches
        const typeMatch = courseType === selectedCourseType;
        const locationMatch = 
          location === selectedLocation || 
          (selectedLocation === 'SOM' && (location === 'Spirit of Math' || location === 'SOM')) ||
          (selectedLocation === 'Harbord' && location === 'Harbord') ||
          (selectedLocation === 'TMU' && location === 'TMU');
        
        console.log(`Template ${template.template_name}: template_type=${templateType} -> mapped_type=${courseType} (want ${selectedCourseType}), location=${location} (want ${selectedLocation}) -> type_match=${typeMatch}, location_match=${locationMatch}`);
        
        return typeMatch && locationMatch;
      }).map((template: any) => {
        // Convert template to expected classroom format
        const templateType = template.classroom_info?.class_type || 'Unknown';
        const mappedType = mapTemplateTypeToConnectTeamType(templateType);
        
        return {
          external_id: template.template_name, // Use template name as ID
          name: template.template_name,
          class_type: mappedType, // Use mapped type for ConnectTeam
          location: template.classroom_info?.location || 'Unknown',
          max_enrollment: template.classroom_info?.max_enrollment || 25,
          template: template // Keep reference to full template
        };
      });
      
      console.log(`Found ${matchingClassrooms.length} matching classrooms:`, matchingClassrooms.map((c: any) => `${c.external_id} (${c.class_type} at ${c.location})`));
      
      const previews: ShiftPreview[] = [];
      
      for (const classroom of matchingClassrooms) {
        console.log(`Processing template ${classroom.external_id}...`);
        
        const template = classroom.template;
        const courses = template.courses || [];
        
        console.log(`  Template has ${courses.length} courses`);
        
        // Create shifts based on the session patterns in the template
        for (const course of courses) {
          const sessionPatterns = course.session_patterns || [];
          console.log(`    Course "${course.course_name}" has ${sessionPatterns.length} session patterns`);
          
          for (const pattern of sessionPatterns) {
            // Create a shift based on the session pattern
            const now = new Date();
            const startDateTime = new Date(now.getTime() + 24 * 60 * 60 * 1000); // Tomorrow
            
            // Parse the start time from the pattern
            if (pattern.start_time) {
              const [hours, minutes] = pattern.start_time.split(':').map(Number);
              startDateTime.setHours(hours, minutes, 0, 0);
            } else {
              startDateTime.setHours(9, 0, 0, 0); // Default 9:00 AM
            }
            
            const endDateTime = new Date(startDateTime);
            if (pattern.end_time) {
              const [hours, minutes] = pattern.end_time.split(':').map(Number);
              endDateTime.setHours(hours, minutes, 0, 0);
            } else {
              endDateTime.setHours(17, 0, 0, 0); // Default 5:00 PM
            }
            
            // Determine shift type from session type
            const sessionType = pattern.session_type?.toLowerCase() || '';
            let shiftType: 'Online' | 'Classroom' | 'Pool' = 'Classroom';
            
            if (sessionType.includes('online') || sessionType.includes('zoom')) {
              shiftType = 'Online';
            } else if (sessionType.includes('pool') || selectedCourseType === 'NL' || selectedCourseType === 'SWI' || selectedCourseType === 'Bronze') {
              shiftType = 'Pool';
            }
            
            const preview: ShiftPreview = {
              id: `${classroom.external_id}-${course.course_name}-${pattern.session_type}`,
              courseType: selectedCourseType,
              location: selectedLocation,
              shiftType,
              startTime: startDateTime.toLocaleTimeString('en-US', { 
                hour: '2-digit', 
                minute: '2-digit',
                hour12: true
              }),
              endTime: endDateTime.toLocaleTimeString('en-US', { 
                hour: '2-digit', 
                minute: '2-digit',
                hour12: true
              }),
              startDate: startDateTime.toLocaleDateString('en-US'),
              endDate: endDateTime.toLocaleDateString('en-US'),
              duration: formatDuration(endDateTime.getTime() - startDateTime.getTime()),
              classroomId: classroom.external_id,
              sessionIds: [] // Template-based, no specific session IDs yet
            };
            
            previews.push(preview);
            console.log(`    Created shift: ${preview.shiftType} shift "${course.course_name}" (${pattern.session_type}) on ${preview.startDate} from ${preview.startTime} to ${preview.endTime}`);
          }
        }
        
        // If no courses in template, create a basic shift
        if (courses.length === 0) {
          console.log(`  Template has no courses, creating basic shift`);
          
          const now = new Date();
          const startDateTime = new Date(now.getTime() + 24 * 60 * 60 * 1000); // Tomorrow
          startDateTime.setHours(9, 0, 0, 0); // 9:00 AM
          
          const endDateTime = new Date(startDateTime);
          endDateTime.setHours(17, 0, 0, 0); // 5:00 PM
          
          const shiftType = selectedCourseType === 'NL' ? 'Pool' : 
                           selectedCourseType === 'SWI' ? 'Pool' : 
                           selectedCourseType === 'Bronze' ? 'Pool' : 
                           'Classroom';
          
          const preview: ShiftPreview = {
            id: `${classroom.external_id}-basic`,
            courseType: selectedCourseType,
            location: selectedLocation,
            shiftType: shiftType as 'Online' | 'Classroom' | 'Pool',
            startTime: startDateTime.toLocaleTimeString('en-US', { 
              hour: '2-digit', 
              minute: '2-digit',
              hour12: true
            }),
            endTime: endDateTime.toLocaleTimeString('en-US', { 
              hour: '2-digit', 
              minute: '2-digit',
              hour12: true
            }),
            startDate: startDateTime.toLocaleDateString('en-US'),
            endDate: endDateTime.toLocaleDateString('en-US'),
            duration: formatDuration(endDateTime.getTime() - startDateTime.getTime()),
            classroomId: classroom.external_id,
            sessionIds: []
          };
          
          previews.push(preview);
          console.log(`    Created basic shift: ${preview.shiftType} shift on ${preview.startDate} from ${preview.startTime} to ${preview.endTime}`);
        }
      }
      
      console.log(`Created ${previews.length} total shift previews`);
      
      // Sort by date and time
      const sortedPreviews = previews.sort((a, b) => {
        const dateA = new Date(`${a.startDate} ${a.startTime}`);
        const dateB = new Date(`${b.startDate} ${b.startTime}`);
        return dateA.getTime() - dateB.getTime();
      });
      
      setShiftPreviews(sortedPreviews);
      
      if (sortedPreviews.length > 0) {
        setError(null); // Clear any previous errors
      } else {
        setError(`No classroom templates found for ${selectedCourseType} at ${selectedLocation}. Make sure you have classroom data uploaded in the Course Upload module.`);
      }
      
    } catch (error: any) {
      console.error('Error loading shift previews:', error);
      setError(error.response?.data?.error || 'Failed to load shift previews');
    } finally {
      setLoading(false);
    }
  };

  // TODO: Re-enable shift type determination when we load actual session data from Salesforce
  // const determineShiftType = (sessions: any[]): 'Online' | 'Classroom' | 'Pool' => { ... }

  const formatDuration = (milliseconds: number): string => {
    const hours = Math.floor(milliseconds / (1000 * 60 * 60));
    const minutes = Math.floor((milliseconds % (1000 * 60 * 60)) / (1000 * 60));
    
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    } else {
      return `${minutes}m`;
    }
  };

  const handleEditShift = (shiftId: string) => {
    setEditingShift(shiftId);
  };

  const handleSaveEdit = (shiftId: string) => {
    const editedData = editedShifts[shiftId];
    if (editedData) {
      setShiftPreviews(prev => prev.map(shift => 
        shift.id === shiftId 
          ? { ...shift, ...editedData, isEdited: true }
          : shift
      ));
    }
    setEditingShift(null);
  };

  const handleCancelEdit = () => {
    setEditingShift(null);
  };

  const handleEditChange = (shiftId: string, field: keyof ShiftPreview, value: any) => {
    setEditedShifts(prev => ({
      ...prev,
      [shiftId]: {
        ...prev[shiftId],
        [field]: value
      }
    }));
  };

  const handleCreateShifts = async () => {
    try {
      setCreating(true);
      setError(null);
      
      // Get unique classroom IDs from the shift previews
      const classroomIds = Array.from(new Set(shiftPreviews.map(shift => shift.classroomId)));
      
      if (classroomIds.length === 1) {
        // Single classroom
        const response = await axios.post(`/api/connecteam/create-shift-from-classroom/${classroomIds[0]}`);
        setCreationResults(response.data);
      } else {
        // Multiple classrooms
        const response = await axios.post('/api/connecteam/create-shifts-bulk', {
          classroom_external_ids: classroomIds
        });
        setCreationResults(response.data);
      }
      
      setResultsDialogOpen(true);
      
      if (creationResults?.success) {
        setSuccess(`Successfully created ${creationResults.shifts_created || 0} shift(s) in ConnectTeam`);
      }
      
    } catch (error: any) {
      console.error('Error creating shifts:', error);
      const errorData = error.response?.data || {};
      setCreationResults({
        success: false,
        error: errorData.error || error.message || 'Unknown error',
        error_details: errorData.error_details,
        classroom_id: errorData.classroom_id || 'Unknown',
        classroom_name: errorData.classroom_name || 'Unknown',
        sessions_processed: 0,
        shifts_created: 0,
        created_shifts: []
      });
      setResultsDialogOpen(true);
      setError(errorData.error || error.message || 'Failed to create shifts');
    } finally {
      setCreating(false);
    }
  };

  const handleReset = () => {
    setSelectedCourseType('');
    setSelectedLocation('');
    setShiftPreviews([]);
    setEditedShifts({});
    setEditingShift(null);
    setCreationResults(null);
    setError(null);
    setSuccess(null);
  };

  const getShiftTypeIcon = (type: string) => {
    switch (type) {
      case 'Online': return <ComputerIcon />;
      case 'Pool': return <PoolIcon />;
      case 'Classroom': return <ClassIcon />;
      default: return <SchoolIcon />;
    }
  };

  const getShiftTypeColor = (type: string) => {
    switch (type) {
      case 'Online': return 'primary';
      case 'Pool': return 'info';
      case 'Classroom': return 'secondary';
      default: return 'default';
    }
  };


  if (loading && !jobMappings) {
    return (
      <Container maxWidth="lg">
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
          <Typography variant="body1" sx={{ ml: 2 }}>
            Loading ConnectTeam job mappings...
          </Typography>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box py={3}>
        {/* Header */}
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
          <Typography variant="h4" component="h1" sx={{ display: 'flex', alignItems: 'center' }}>
            <ScheduleIcon sx={{ mr: 2 }} />
            ConnectTeam Scheduler
          </Typography>
          
          <Box display="flex" alignItems="center" gap={2}>
            <Chip
              icon={connectionStatus === 'connected' ? <CheckCircleIcon /> : <ErrorIcon />}
              label={connectionStatus === 'connected' ? 'Connected' : 'Disconnected'}
              color={connectionStatus === 'connected' ? 'success' : 'error'}
            />
            
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={loadJobMappings}
              disabled={loading}
            >
              Refresh
            </Button>
          </Box>
        </Box>

        {/* Error/Success Messages */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        
        {success && (
          <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}

        {/* Control Bar */}
        <Box 
          sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between',
            p: 2,
            mb: 3,
            border: '1px solid #ccc',
            borderRadius: 2,
            backgroundColor: '#fafafa'
          }}
        >
          {/* Left side: Course and Location dropdowns */}
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <FormControl sx={{ minWidth: 180 }}>
              <InputLabel>Course Type</InputLabel>
              <Select
                value={selectedCourseType}
                label="Course Type"
                onChange={(e) => {
                  setSelectedCourseType(e.target.value);
                  setSelectedLocation('');
                  setShiftPreviews([]);
                }}
              >
                {jobMappings && Object.keys(jobMappings.regular_jobs).map(courseType => (
                  <MenuItem key={courseType} value={courseType}>
                    <Box display="flex" alignItems="center">
                      <SchoolIcon sx={{ mr: 1 }} />
                      {courseType}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl sx={{ minWidth: 180 }}>
              <InputLabel>Location</InputLabel>
              <Select
                value={selectedLocation}
                label="Location"
                onChange={(e) => {
                  setSelectedLocation(e.target.value);
                }}
                disabled={!selectedCourseType}
              >
                {selectedCourseType && jobMappings?.regular_jobs[selectedCourseType] && 
                  Object.keys(jobMappings.regular_jobs[selectedCourseType])
                    .filter(location => location !== 'default')
                    .map(location => (
                      <MenuItem key={location} value={location}>
                        <Box display="flex" alignItems="center">
                          <LocationOnIcon sx={{ mr: 1 }} />
                          {location}
                        </Box>
                      </MenuItem>
                    ))
                }
              </Select>
            </FormControl>
          </Box>

          {/* Right side: Preview button */}
          <Button
            variant="contained"
            onClick={loadShiftPreviews}
            disabled={!selectedCourseType || !selectedLocation || loading}
            startIcon={loading ? <CircularProgress size={20} /> : <PreviewIcon />}
            sx={{ minWidth: 140 }}
          >
            {loading ? 'Loading...' : 'Preview'}
          </Button>
        </Box>

        {/* Shift Previews */}
        {shiftPreviews.length > 0 && (
          <Card>
            <CardContent>
                  <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                    Review the shifts that will be created. Click the edit icon to modify individual shifts.
                  </Typography>
                  
                  {shiftPreviews.length > 0 ? (
                    <TableContainer component={Paper} sx={{ mb: 2 }}>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>#</TableCell>
                            <TableCell>Course Type</TableCell>
                            <TableCell>Shift Type</TableCell>
                            <TableCell>Location</TableCell>
                            <TableCell>Date</TableCell>
                            <TableCell>Start Time</TableCell>
                            <TableCell>End Time</TableCell>
                            <TableCell>Duration</TableCell>
                            <TableCell>Actions</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {shiftPreviews.map((shift, index) => (
                            <TableRow key={shift.id}>
                              <TableCell>
                                <Typography variant="body2" fontWeight="bold">
                                  {index + 1}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Chip
                                  icon={<SchoolIcon />}
                                  label={shift.courseType}
                                  size="small"
                                  variant={shift.isEdited ? "filled" : "outlined"}
                                />
                              </TableCell>
                              <TableCell>
                                {editingShift === shift.id ? (
                                  <Select
                                    size="small"
                                    value={editedShifts[shift.id]?.shiftType || shift.shiftType}
                                    onChange={(e) => handleEditChange(shift.id, 'shiftType', e.target.value)}
                                  >
                                    <MenuItem value="Online">Online</MenuItem>
                                    <MenuItem value="Classroom">Classroom</MenuItem>
                                    <MenuItem value="Pool">Pool</MenuItem>
                                  </Select>
                                ) : (
                                  <Chip
                                    icon={getShiftTypeIcon(shift.shiftType)}
                                    label={shift.shiftType}
                                    color={getShiftTypeColor(shift.shiftType) as any}
                                    size="small"
                                  />
                                )}
                              </TableCell>
                              <TableCell>
                                <Box display="flex" alignItems="center">
                                  <LocationOnIcon sx={{ mr: 0.5, fontSize: 16 }} />
                                  {shift.location}
                                </Box>
                              </TableCell>
                              <TableCell>{shift.startDate}</TableCell>
                              <TableCell>
                                {editingShift === shift.id ? (
                                  <TextField
                                    size="small"
                                    type="time"
                                    value={editedShifts[shift.id]?.startTime || shift.startTime}
                                    onChange={(e) => handleEditChange(shift.id, 'startTime', e.target.value)}
                                  />
                                ) : (
                                  <Box display="flex" alignItems="center">
                                    <AccessTimeIcon sx={{ mr: 0.5, fontSize: 16 }} />
                                    {shift.startTime}
                                  </Box>
                                )}
                              </TableCell>
                              <TableCell>
                                {editingShift === shift.id ? (
                                  <TextField
                                    size="small"
                                    type="time"
                                    value={editedShifts[shift.id]?.endTime || shift.endTime}
                                    onChange={(e) => handleEditChange(shift.id, 'endTime', e.target.value)}
                                  />
                                ) : (
                                  <Box display="flex" alignItems="center">
                                    <AccessTimeIcon sx={{ mr: 0.5, fontSize: 16 }} />
                                    {shift.endTime}
                                  </Box>
                                )}
                              </TableCell>
                              <TableCell>{shift.duration}</TableCell>
                              <TableCell>
                                {editingShift === shift.id ? (
                                  <Box display="flex" gap={0.5}>
                                    <IconButton
                                      size="small"
                                      onClick={() => handleSaveEdit(shift.id)}
                                      color="primary"
                                    >
                                      <SaveIcon />
                                    </IconButton>
                                    <IconButton
                                      size="small"
                                      onClick={handleCancelEdit}
                                    >
                                      <CancelIcon />
                                    </IconButton>
                                  </Box>
                                ) : (
                                  <IconButton
                                    size="small"
                                    onClick={() => handleEditShift(shift.id)}
                                  >
                                    <EditIcon />
                                  </IconButton>
                                )}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  ) : (
                    <Alert severity="info" sx={{ mb: 2 }}>
                      No shifts found for the selected course type and location.
                    </Alert>
                  )}
                  
                  <Box display="flex" justifyContent="center" sx={{ mt: 3 }}>
                    <Button
                      variant="contained"
                      size="large"
                      onClick={handleCreateShifts}
                      disabled={shiftPreviews.length === 0 || creating}
                      startIcon={creating ? <CircularProgress size={20} /> : <SendIcon />}
                      sx={{ minWidth: 200 }}
                    >
                      {creating ? 'Creating...' : `Create ${shiftPreviews.length} Shift(s)`}
                    </Button>
                  </Box>
            </CardContent>
          </Card>
        )}

        {/* Results Dialog */}
        <Dialog
          open={resultsDialogOpen}
          onClose={() => setResultsDialogOpen(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            <Box display="flex" alignItems="center">
              {creationResults?.success ? (
                <CheckCircleIcon color="success" sx={{ mr: 1 }} />
              ) : (
                <ErrorIcon color="error" sx={{ mr: 1 }} />
              )}
              Shift Creation Results
            </Box>
          </DialogTitle>
          
          <DialogContent>
            {creationResults?.success ? (
              <Alert severity="success" sx={{ mb: 2 }}>
                Successfully created {creationResults.shifts_created} shift(s) in ConnectTeam!
              </Alert>
            ) : (
              <Alert severity="error" sx={{ mb: 2 }}>
                {creationResults?.error || 'Failed to create shifts'}
              </Alert>
            )}
            
            {creationResults && (
              <Box>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>Sessions Processed:</strong> {creationResults.sessions_processed}
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>Shifts Created:</strong> {creationResults.shifts_created}
                </Typography>
                
                {creationResults.error_details && (
                  <Box mt={2}>
                    <Typography variant="body2" color="textSecondary">
                      Error Details:
                    </Typography>
                    <pre style={{ fontSize: '12px', overflow: 'auto' }}>
                      {JSON.stringify(creationResults.error_details, null, 2)}
                    </pre>
                  </Box>
                )}
              </Box>
            )}
          </DialogContent>
          
          <DialogActions>
            <Button onClick={() => setResultsDialogOpen(false)}>
              Close
            </Button>
            {creationResults?.success && (
              <Button 
                variant="contained" 
                onClick={() => {
                  setResultsDialogOpen(false);
                  handleReset();
                }}
              >
                Create More Shifts
              </Button>
            )}
          </DialogActions>
        </Dialog>
      </Box>
    </Container>
  );
};

export default ConnectTeamScheduler;